import { Component, Input, NgZone, AfterViewInit, OnDestroy, ElementRef, PLATFORM_ID, Inject } from '@angular/core';
import * as am5 from '@amcharts/amcharts5';
import * as am5xy from '@amcharts/amcharts5/xy';
import am5themes_Animated from '@amcharts/amcharts5/themes/Animated';
import { isPlatformBrowser } from '@angular/common';
import { GlobalContants } from 'src/app/common/constants/global.contants';
import { SharedService } from 'src/app/common/services/shared.service';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-comparable-chart',
  templateUrl: './comparable-chart.component.html',
  styleUrls: ['./comparable-chart.component.scss']
})
export class ComparableChartComponent implements AfterViewInit, OnDestroy {
  @Input() chartId: string = 'chartdiv';
  @Input() chartData: any[] = [];
  @Input() laguagecode: string = '';

  subjectSeriesColor: string = "#2372F0";
  comparableSeriesColor: string = "#FF6C16";
  CurrencyCode: string = '';

  private root!: am5.Root;
  private xAxis!: am5xy.CategoryAxis<any>;
  private subjectSeries!: am5xy.LineSeries;
  private comparableSeries!: am5xy.LineSeries;

  private expandedYears: Set<string> = new Set();

  constructor(
    private translate: TranslateService,
    private sharedService: SharedService,
    private zone: NgZone,
    private el: ElementRef,
    @Inject(PLATFORM_ID) private platformId: any
  ) {}

  ngAfterViewInit(): void {
    this.CurrencyCode = this.sharedService.getStoreValue(GlobalContants.StoreKey.CurrencyCode) || 'AED';

    this.zone.runOutsideAngular(() => {
      setTimeout(() => {
        this.createChart();

        if (this.chartData?.length > 0) {
          const firstYear = this.getYearFromQuarter(this.chartData[0].quarter);
          this.toggleYearExpansion(firstYear);
        }
      }, 100);
    });
  }

  private createChart() {
    if (!isPlatformBrowser(this.platformId)) return;

    const chartDiv = this.el.nativeElement.querySelector(`#${this.chartId}`);
    if (!chartDiv) return;

    this.root = am5.Root.new(chartDiv);
    this.root.setThemes([am5themes_Animated.new(this.root)]);

    let chart = this.root.container.children.push(
      am5xy.XYChart.new(this.root, {
        panX: true,
        panY: false,
        wheelX: "panX",
        wheelY: "zoomX",
        paddingBottom: 40,
        paddingTop: 0,
        paddingLeft: 10,
        paddingRight: 10,
        layout: this.root.verticalLayout,
        pinchZoomX: false
      })
    );

    var numberFormatter = am5.NumberFormatter.new(this.root, {
      numberFormat: "#.#"
    });

    let xRenderer = am5xy.AxisRendererX.new(this.root, {
      minGridDistance: 50,
      pan: "none",
      cellStartLocation: 0.1,
      cellEndLocation: 0.1    
    });

    this.xAxis = chart.xAxes.push(
      am5xy.CategoryAxis.new(this.root, {
        categoryField: "label",
        renderer: xRenderer
      })
    );

    this.xAxis.get("renderer").labels.template.setAll({
      rotation: -15,
      centerY: am5.p50,
      centerX: am5.p50,
      paddingRight: 10,
      fontSize: 12,
      fill: am5.color(0x555555),
      oversizedBehavior: "fit",
      textAlign: "center",
      minScale: 0.5,
      forceHidden: false,
      backgroundOpacity: 0,
       interactive: false
    });
    //   this.xAxis.get("renderer").labels.template.states.create("hover", {
    //   backgroundOpacity: 0
    // });
    this.xAxis.get("renderer").labels.template.adapters.add("text", (text: any, target: any) => {
      const data = target.dataItem?.dataContext as any;
      if (data?.isYear) {
        target.set("fontWeight", "700");
        target.set("fontSize", 14);
      } else {
        target.set("fontWeight", "400");
        target.set("fontSize", 11);
      }
      target.set("forceHidden", false);
      return text;
    });

    let yAxis = chart.yAxes.push(
      am5xy.ValueAxis.new(this.root, {
        min: 0,
        extraMax: 0.2,
        renderer: am5xy.AxisRendererY.new(this.root, { strokeOpacity: 0 }),
        numberFormatter: numberFormatter
      })
    );

    yAxis.get("renderer").grid.template.setAll({
      stroke: am5.color(0xf5f5f5),
      strokeOpacity: 1,
      visible: true
    });
     xRenderer.grid.template.set("visible", false);
    chart.children.unshift(
      am5.Label.new(this.root, {
        text: `${this.CurrencyCode}/sqm`,
        x: 5,
        y: -8,
        fontWeight: "400",
        fontSize: 12,
        fill: am5.color(0x555555)
      })
    );

    const createSeries = (name: string, field: string, color: string) => {
      const series = chart.series.push(
        am5xy.LineSeries.new(this.root, {
          name,
          xAxis: this.xAxis,
          yAxis,
          valueYField: field,
          categoryXField: "label",
          stroke: am5.color(color),
          tooltip: am5.Tooltip.new(this.root, {
            labelHTML: `<div class='text_tooltip_white'>{valueY.formatNumber('#,##,###.#')} ${this.CurrencyCode}/sqm</div>`
          }),
          fill: am5.color(color),
          minBulletDistance: 5
        })
      );

      series.bullets.push((root, s, dataItem: any) => {
        const ctx = dataItem.dataContext as any;

        if (ctx.isYear) {
          const container = am5.Container.new(root, { interactive: true });

          const isExpanded = this.expandedYears.has(ctx.year);

          const circle = container.children.push(
            am5.Circle.new(root, {
              radius: 6,
              fill: isExpanded ? am5.color(0x000000) : am5.color(color),
              stroke: am5.color(color),
              strokeWidth: 2
            })
          );

          circle.set("forceHidden", false);

          container.events.on("click", () => this.toggleYearExpansion(ctx.year));
          container.set("cursorOverStyle", "pointer");

          return am5.Bullet.new(root, { sprite: container });
        } else {
          const circle = am5.Circle.new(root, {
            radius: 4,
            fill: am5.color(color)
          });
          circle.set("forceHidden", false);
          return am5.Bullet.new(root, { sprite: circle });
        }
      });

      return series;
    };

    this.subjectSeries = createSeries(
      this.translate.instant('reports.Subject_Property'),
      "subject",
      this.subjectSeriesColor
    );

    this.comparableSeries = createSeries(
      `${this.translate.instant('reports.Comparable_Properties')} [fontSize:10px]${this.translate.instant('reports.based_on_filter')}[/]`,
      "comparable",
      this.comparableSeriesColor
    );

    const legend = chart.children.push(
      am5.Legend.new(this.root, {
        centerX: am5.p50,
        x: am5.p50,
        layout: this.root.horizontalLayout,
        y: am5.percent(100),
      })
    );

    legend.itemContainers.template.setAll({
      paddingTop: 0,
      paddingBottom: 0,
      paddingLeft: 0,
      paddingRight: -35
    });

    legend.data.setAll([this.subjectSeries, this.comparableSeries]);

      chart.set("cursor", am5xy.XYCursor.new(this.root, {
      behavior: "none", 
      
    }));
    this.updateChartDisplay();
  }

  private toggleYearExpansion(year: string) {
    if (this.expandedYears.has(year)) {
      this.expandedYears.delete(year);
    } else {
      this.expandedYears.add(year);
    }
    this.updateChartDisplay();
  }

  private updateChartDisplay() {
    const finalData: any[] = [];

    const groupedByYear = this.groupDataByYear(this.chartData);

    groupedByYear.forEach((group) => {
      const year = group.year;
      const shortYear = year.slice(-2);

      finalData.push({
        label: year,
        subject: group.avgSubject,
        comparable: group.avgComparable,
        year: year,
        isYear: true
      });

      if (this.expandedYears.has(year)) {
        group.items.forEach((item: { quarter: string; subject: any; comparable: any; }) => {
          const q = item.quarter.split('-')[0];
          finalData.push({
            label: `${q}'${shortYear}`,
            subject: item.subject,
            comparable: item.comparable,
            year: year,
            isYear: false
          });
        });
      }
    });

    this.xAxis.data.setAll(finalData);
    this.subjectSeries.data.setAll(finalData);
    this.comparableSeries.data.setAll(finalData);

    this.xAxis.events.once("datavalidated", () => {
      this.xAxis.zoom(0, 1, 800);
    });
    this.xAxis.set("minZoomCount", 2);
    this.xAxis.set("maxZoomCount", finalData.length);
  }

  private groupDataByYear(data: any[]) {
    const groups: any[] = [];

    data.forEach(item => {
      const [q, year] = item.quarter.split('-');
      let group = groups.find(g => g.year === year);

      if (!group) {
        group = { year, items: [], avgSubject: item.subject, avgComparable: item.comparable };
        groups.push(group);
      }

      group.items.push(item);
      if (item.subject !== null) group.avgSubject = item.subject;
      if (item.comparable !== null) group.avgComparable = item.comparable;
    });

    return groups;
  }

  private getYearFromQuarter(quarter: string): string {
    return quarter.split('-')[1] || 'Unknown';
  }

  resetExpandedYears() {
    this.expandedYears.clear();
    this.updateChartDisplay();
  }

  get hasExpandedYears(): boolean {
    return this.expandedYears.size > 0;
  }

  ngOnDestroy(): void {
    this.zone.runOutsideAngular(() => {
      if (this.root) this.root.dispose();
    });
  }
}